import { Box, Flex, Heading, Select, Spinner, Text, Wrap, WrapItem } from '@chakra-ui/react'
import React, { useState } from 'react'
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import {
  agentScopeSuffix,
  useAgentName,
  useFeedbackSummary,
} from '../../hooks/analytics/useAnalytics'
import { AgentFilter } from '../../types/analyticsTypes'
import { useChartPalette } from './useChartPalette'

interface FeedbackDonutChartProps {
  agentId: AgentFilter
}

const COLORS = {
  good: '#38A169',
  neutral: '#A0AEC0',
  bad: '#E53E3E',
}

// Timeframe presets for the feedback distribution. `null` is all time; the
// donut counts answers, not content, so it is not bound by the visibility cap
// that limits the negative-feedback table.
const TIMEFRAME_OPTIONS: { label: string; days: number | null }[] = [
  { label: 'All time', days: null },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 7 days', days: 7 },
]
const ALL_TIME = 'all'

const FeedbackDonutChart: React.FC<FeedbackDonutChartProps> = ({ agentId }) => {
  const [days, setDays] = useState<number | null>(null)
  const agentName = useAgentName(agentId)
  const palette = useChartPalette()
  const { data: summary, isLoading, isError } = useFeedbackSummary(agentId, days)

  const timeframeSelect = (
    <Select
      size='sm'
      width='auto'
      value={days ?? ALL_TIME}
      onChange={event =>
        setDays(event.target.value === ALL_TIME ? null : Number(event.target.value))
      }
    >
      {TIMEFRAME_OPTIONS.map(option => (
        <option key={option.label} value={option.days ?? ALL_TIME}>
          {option.label}
        </option>
      ))}
    </Select>
  )

  if (isLoading) {
    return (
      <Flex
        bg='app.bg'
        borderWidth='1px'
        borderColor='app.border'
        borderRadius='lg'
        height='340px'
        align='center'
        justify='center'
      >
        <Spinner />
      </Flex>
    )
  }
  if (isError || !summary) {
    return (
      <Box bg='app.bg' borderWidth='1px' borderColor='app.border' borderRadius='lg' p={5}>
        <Text color='red.500'>Could not load this widget.</Text>
      </Box>
    )
  }

  const data = [
    { key: 'good', label: 'Thumbs up', value: summary.good, percentage: summary.good_percentage, color: COLORS.good },
    { key: 'neutral', label: 'No feedback', value: summary.neutral, percentage: summary.neutral_percentage, color: COLORS.neutral },
    { key: 'bad', label: 'Thumbs down', value: summary.bad, percentage: summary.bad_percentage, color: COLORS.bad },
  ]
  const scope = days === null ? 'All answers' : `Answers in the last ${days} days`

  return (
    <Box bg='app.bg' borderWidth='1px' borderColor='app.border' borderRadius='lg' p={5}>
      <Flex justify='space-between' align='flex-start' mb={1}>
        <Heading size='sm'>Feedback distribution{agentScopeSuffix(agentName)}</Heading>
        {timeframeSelect}
      </Flex>
      <Text fontSize='sm' color='app.text.muted' mb={4}>
        {scope}, {summary.total} total
      </Text>
      {summary.total === 0 ? (
        <Text color='app.text.muted'>No answers in this period.</Text>
      ) : (
        <>
          <Box position='relative' height='260px'>
            <ResponsiveContainer width='100%' height='100%'>
              <PieChart>
                <Pie
                  data={data}
                  dataKey='value'
                  nameKey='label'
                  cx='50%'
                  cy='50%'
                  innerRadius={60}
                  outerRadius={90}
                >
                  {data.map(entry => (
                    <Cell key={entry.key} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  {...palette.tooltipProps}
                  formatter={(value, _name, item) => [
                    `${value} (${item?.payload?.percentage}%)`,
                    item?.payload?.label,
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
            <Box
              position='absolute'
              top='50%'
              left='50%'
              transform='translate(-50%, -50%)'
              textAlign='center'
              pointerEvents='none'
            >
              <Text fontSize='2xl' fontWeight='bold' lineHeight='1'>
                {summary.total}
              </Text>
              <Text fontSize='xs' color='app.text.muted'>
                answers
              </Text>
            </Box>
          </Box>
          <Wrap spacing={4} justify='center' mt={4}>
            {data.map(entry => (
              <WrapItem key={entry.key} alignItems='center'>
                <Box w='10px' h='10px' borderRadius='sm' bg={entry.color} mr={2} />
                <Text fontSize='sm' color='app.text.secondary'>
                  {entry.label}: {entry.value} ({entry.percentage}%)
                </Text>
              </WrapItem>
            ))}
          </Wrap>
        </>
      )}
    </Box>
  )
}

export default FeedbackDonutChart
